import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider, useApp } from './context/AppContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import ProfileSelect from './pages/ProfileSelect';
import Shell from './components/Shell';
import Home from './pages/Home';
import Movies from './pages/Movies';
import TVShows from './pages/TVShows';
import SearchPage from './pages/SearchPage';
import MyList from './pages/MyList';
import Originals from './pages/Originals';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Subscribe from './pages/Subscribe';
import SubscribeSuccess from './pages/SubscribeSuccess';
import PPVSuccess from './pages/PPVSuccess';
import Studio from './pages/Studio';
import About from './pages/About';
import Contact from './pages/Contact';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Account from './pages/Account';
import { Toaster } from './components/ui/sonner';

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-12 h-12 rounded-full border-4 border-white/10 border-t-[#E50914] animate-spin" />
  </div>
);

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const RequireProfile = ({ children }) => {
  const { profile } = useApp();
  if (!profile) return <Navigate to="/profiles" replace />;
  return children;
};

const RequireAdmin = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  const location = useLocation();
  // Detect session_id SYNCHRONOUSLY during render (before ProtectedRoute runs)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route
        path="/profiles"
        element={
          <RequireAuth>
            <ProfileSelect />
          </RequireAuth>
        }
      />
      <Route
        path="/account"
        element={
          <RequireAuth>
            <Account />
          </RequireAuth>
        }
      />
      <Route
        path="/subscribe"
        element={
          <RequireAuth>
            <Subscribe />
          </RequireAuth>
        }
      />
      <Route
        path="/subscribe/success"
        element={
          <RequireAuth>
            <SubscribeSuccess />
          </RequireAuth>
        }
      />
      <Route
        path="/ppv/success"
        element={
          <RequireAuth>
            <PPVSuccess />
          </RequireAuth>
        }
      />
      <Route
        path="/studio"
        element={
          <RequireAdmin>
            <Studio />
          </RequireAdmin>
        }
      />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <RequireProfile>
              <Shell>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/movies" element={<Movies />} />
                  <Route path="/tv" element={<TVShows />} />
                  <Route path="/originals" element={<Originals />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/mylist" element={<MyList />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Shell>
            </RequireProfile>
          </RequireAuth>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <AuthProvider>
          <SubscriptionProvider>
            <AppProvider>
              <AppRoutes />
              <Toaster position="top-right" theme="dark" />
            </AppProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
